/* eslint-disable @typescript-eslint/no-unused-vars */
import { useLocation, useNavigate } from "react-router-dom";
import {
  IconPackage,
  IconUser,
  IconBuilding,
  IconDownload,
  IconEye,
} from "@tabler/icons-react";
import ListView from "../../components/common/ListView";
import NoDataFound from "../../components/common/NoDataFound";
import { useEffect, useState } from "react";
import formatDate from "../../utilities/helper";
import { handleDownload } from "../../utilities/downloadHelper";

interface SearchResultItem {
  entityType: "user" | "order" | "client";
  entityId: string;
  displayTitle: string;
  subText?: string;
  awbNumber?: string;
  orderName?: string;
  referenceNumber?: string;
  receiverCountryName?: string;
  receiverCityName?: string;
  receiverDistrictName?: string;
  status?: string;
  createdAt?: string;
}

// Helper to get entity icon
const getEntityIcon = (type: string) => {
  switch (type) {
    case "order":
      return <IconPackage size={18} stroke={1.5} className="text-blue-500" />;
    case "user":
      return <IconUser size={18} stroke={1.5} className="text-green-500" />;
    case "client":
      return (
        <IconBuilding size={18} stroke={1.5} className="text-yellow-500" />
      );
    default:
      return <IconPackage size={18} stroke={1.5} className="text-gray-500" />;
  }
};

// Helper to get navigation path
const getEntityPath = (type: string, id: string): string => {
  switch (type) {
    case "order":
      return `/orders/${id}`;
    case "user":
      return `/users/${id}`;
    case "client":
      return `/clients/${id}`;
    default:
      return "/dashboard";
  }
};

const getStatusButtonClass = (status: string) => {
  const normalized = status?.toLowerCase();

  switch (normalized) {
    case "draft": // neutral / gray
      return "bg-gray-100 border-gray-400 text-gray-700";
    case "new_order": // info → blue
      return "bg-blue-100 border-blue-500 text-blue-700";
    case "processing": // progress → indigo
      return "bg-indigo-100 border-indigo-500 text-indigo-700";
    case "ready_for_pickup": // waiting → amber/yellow
      return "bg-amber-100 border-amber-500 text-amber-700";
    case "dispatched": // moving → teal/greenish
      return "bg-teal-100 border-teal-500 text-teal-700";
    case "in_transit": // traveling → sky
      return "bg-sky-100 border-sky-500 text-sky-700";
    case "out_for_delivery": // active → orange
      return "bg-orange-100 border-orange-500 text-orange-700";
    case "delivered": // success → green
      return "bg-green-100 border-green-600 text-green-700";
    case "undelivered": // warning → amber
      return "bg-amber-100 border-amber-600 text-amber-700";
    case "returned": // secondary issue → purple/pink
      return "bg-purple-100 border-purple-500 text-purple-700";
    case "cancelled": // error → red
      return "bg-red-100 border-red-600 text-red-700";
    case "failed": // danger → rose
      return "bg-rose-100 border-rose-600 text-rose-700";
    case "lost": // dark/neutral → zinc
      return "bg-zinc-100 border-zinc-500 text-zinc-700";
    case "deleted": // destructive → dark red
      return "bg-red-100 border-red-700 text-red-800";
    default: // fallback neutral
      return "bg-gray-100 border-gray-400 text-gray-700";
  }
};

const formatStatus = (status?: string) => {
  if (!status) return ""; // fallback
  return status
    .toLowerCase()
    .split("_")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
};

// Helper to format timestamp
const timeAgo = (dateString: string) => {
  if (!dateString) return "";
  const date = new Date(dateString);
  const now = new Date();
  const diff = Math.floor((now.getTime() - date.getTime()) / 1000);

  if (diff < 60) return `${diff}s ago`;
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  if (diff < 2592000) return `${Math.floor(diff / 86400)}d ago`;
  return date.toLocaleDateString();
};

type SearchData = {
  entityId: string;
};

export default function SearchResults() {
  const location = useLocation();
  const navigate = useNavigate();
  const { searchResults, searchQuery } = (location.state as {
    searchResults: SearchResultItem[];
    searchQuery: string;
  }) || {
    searchResults: [],
    searchQuery: "",
  };

  const [selectedRows, setSelectedRows] = useState<string[]>([]);
  const [allSelectedData, setAllSelectedData] = useState<SearchData[]>([]);
  const [showCheckboxes, setShowCheckboxes] = useState<boolean>(false);

  const [columnsData, setColumnsData] = useState([
    { text: "Select", value: "select", isShow: showCheckboxes },
    { text: "S/N", value: "sn", isShow: true },
    { text: "AWB Number", value: "awbNumber", isShow: true },
    { text: "Reference", value: "referenceNumber", isShow: true },

    { text: "Date", value: "createdAt", isShow: true },
    { text: "Status", value: "status", isShow: true },
  ]);

  useEffect(() => {
    if (searchResults) {
      setAllSelectedData((prev) => {
        const filteredPrev = prev.filter((item: any) =>
          selectedRows.includes(item.entityId),
        );
        const newSelected = searchResults.filter(
          (s: any) =>
            selectedRows.includes(s.entityId) &&
            !filteredPrev.some((item) => item.entityId === s.entityId),
        );
        return [...filteredPrev, ...newSelected];
      });
    }
  }, [searchResults, selectedRows]);

  const handleSelectRow = (entityId: string) => {
    setSelectedRows((prev) =>
      prev.includes(entityId)
        ? prev.filter((rowId) => rowId !== entityId)
        : [...prev, entityId],
    );
  };

  const handleSelectAll = () => {
    if (selectedRows.length === searchResults?.length) {
      setSelectedRows((prev) =>
        prev.filter(
          (entityId) =>
            !searchResults.some((c: any) => c.entityId === entityId),
        ),
      );
    } else {
      const newSelections =
        searchResults
          ?.filter((c: any) => !selectedRows.includes(c.entityId))
          .map((c: any) => c.entityId) || [];
      setSelectedRows((prev) => [...prev, ...newSelections]);
    }
  };

  const mapDataToExport = (c: any, index: number) => ({
    "S/N": index + 1,
    "AWB Number": c.awbNumber,
    Reference: c.referenceNumber,
    Country: c.receiverCountryName,
    City: c.receiverCityName,
    District: c.receiverDistrictName,
    Date: formatDate(c.createdAt),
    Status: formatStatus(c.status),
  });

  const handleResultClick = (item: SearchResultItem) => {
    const path = getEntityPath(item.entityType, item.awbNumber || "");
    navigate(path);
  };

  return (
    <>
      <div className="px-6 pb-4">
        <div className="flex items-center">
          <div className=" bg-[#F7F6F2] flex items-center justify-between px-6 py-3 w-full rounded-tl-3xl rounded-tr-[40px] ">
            <h1 className="text-xl text-[#212121] tracking-[-0.5px] font-medium">
              Search History
            </h1>
          </div>
          <div className="relative ml-5">
            <div className=" flex items-center gap-2 justify-end rounded-full">
              <button
                onClick={() => {
                  if (!showCheckboxes) {
                    setShowCheckboxes(true);
                    setColumnsData((prev) =>
                      prev.map((col) =>
                        col.value === "select" ? { ...col, isShow: true } : col,
                      ),
                    );
                    return;
                  }

                  if (selectedRows.length > 0) {
                    handleDownload({
                      selectedRows,
                      allSelectedData,
                      setSelectedRows,
                      setAllSelectedData,
                      showCheckboxes,
                      setShowCheckboxes,
                      setColumnsData,
                      mapDataToExport,
                      fileName: "search_history.xlsx",
                    });
                  }

                  if (selectedRows.length === 0) {
                    setShowCheckboxes(false);
                    setColumnsData((prev) =>
                      prev.map((col) =>
                        col.value === "select"
                          ? { ...col, isShow: false }
                          : col,
                      ),
                    );
                  }
                }}
                className={`flex items-center gap-1 p-2.5 text-sm rounded-full border border-[#FFFFFF] ${
                  showCheckboxes && selectedRows.length === 0
                    ? "bg-[#F7F6F2]/50 text-[#404040]/50"
                    : "bg-[#F7F6F2] text-[#404040]"
                }`}
              >
                <IconDownload stroke={1.5} size={24} />
                {selectedRows.length > 0 && (
                  <span>({selectedRows.length})</span>
                )}
              </button>
            </div>
          </div>
        </div>

        {searchResults.length > 0 ? (
          <ListView
            data={searchResults}
            columns={[
              ...(showCheckboxes
                ? [
                    {
                      label: (
                        <label className="relative flex items-center cursor-pointer">
                          <input
                            type="checkbox"
                            checked={
                              searchResults?.length > 0 &&
                              searchResults?.every((c: any) =>
                                selectedRows.includes(c.entityId),
                              )
                            }
                            onChange={handleSelectAll}
                            className="peer h-5 w-5 cursor-pointer appearance-none rounded border border-slate-300 checked:border-[#FBD037] 
                        bg-white checked:bg-[#FBD037] checked:text-white disabled:cursor-not-allowed"
                          />
                          <span
                            className="absolute text-xs font-bold text-white peer-checked:opacity-100 opacity-0 
                            top-1/2 left-[10px] transform -translate-x-1/2 -translate-y-1/2 pointer-events-none"
                          >
                            ✓
                          </span>
                        </label>
                      ),
                      render: (c: any) => (
                        <label className="relative flex items-center cursor-pointer">
                          <input
                            type="checkbox"
                            checked={selectedRows.includes(c.entityId)}
                            onChange={() => handleSelectRow(c.entityId)}
                            className="peer h-5 w-5 cursor-pointer appearance-none rounded border border-slate-300 checked:border-[#FBD037] 
                        bg-white checked:bg-[#FBD037] checked:text-white disabled:cursor-not-allowed"
                          />
                          <span
                            className="absolute text-xs font-bold text-white peer-checked:opacity-100 opacity-0 
                                top-1/2 left-[10px] transform -translate-x-1/2 -translate-y-1/2 pointer-events-none"
                          >
                            ✓
                          </span>
                        </label>
                      ),
                    },
                  ]
                : []),
              { label: "#", render: (_, i) => i + 1 },
              {
                label: "AWB Number",
                render: (item) => (
                  <div className="truncate max-w-[200px]">{item.awbNumber}</div>
                ),
              },
              {
                label: "Reference",
                render: (item) => item.referenceNumber || "",
              },
              {
                label: "Receiver Country",
                render: (item) => item.receiverCountryName || "",
              },
              { label: "Receiver City", render: (item) => item.receiverCityName || "" },
              {
                label: "Receiver District",
                render: (item) => item.receiverDistrictName || "",
              },
              {
                label: "Created",
                render: (item) => formatDate(item.createdAt || ""),
              },
              {
                label: "Status",
                render: (item) => (
                  <div className="flex items-center justify-center">
                    <p
                      className={`bg-[#FBD037]/20 border text-sm px-4 text-center py-2 rounded-full ${getStatusButtonClass(
                        item?.status || "",
                      )}`}
                    >
                      {formatStatus(item?.status)}
                    </p>
                  </div>
                ),
              },
              {
                label: "Actions",
                render: (item) => (
                  <div className="text-center">
                    <button
                      onClick={() => handleResultClick(item)}
                      className="p-2 border rounded-full bg-[#F7F6F2]"
                    >
                      <IconEye size={22} />
                    </button>
                  </div>
                ),
              },
            ]}
          />
        ) : (
          <NoDataFound />
        )}
      </div>
    </>
  );
}
